import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

import { selectStatusSummaryPayload } from '../../../app/state/selectors';
import { ReportContentItem } from '../../models/run.model';
import { CalculationResult, RunStatus } from '../../models/result.model';
import { adjustedColumnClass, adjustedColumnLabel, displayOrDash } from '../../utils/helper';
import { getStatusClassFromResult } from '../../utils/assay-helper ';

type StatusAssayData = {
  reportContentItems: ReportContentItem;
  runStatus?: RunStatus;
  valueAssigned?: string;
  isMeanColumn?: boolean;
};

@Component({
  selector: 'app-status-summary-dialog',
  templateUrl: './status-summary-dialog.component.html',
  styleUrl: './status-summary-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatusSummaryDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('statusSummaryScroll', { static: false }) statusSummaryScrollRef?: ElementRef<HTMLDivElement>;
  calResponseCombinedData: StatusAssayData[] = [];
  refKitLot = '';
  /** Whether the adjusted columns hold a Calibrator Adjustment's Final run. */
  isFinal = false;

  get adjustedLabel(): string {
    return adjustedColumnLabel(this.isFinal);
  }
  readonly getStatusClassFromResult = getStatusClassFromResult;
  private readonly destroy$ = new Subject<boolean>();
  private readonly internalQcBeadRegions = new Set<number>([34, 98]);

  constructor(private readonly store: Store) {}

  ngOnInit(): void {
    this.store.select(selectStatusSummaryPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {
        this.refKitLot = payload.refKitLot ?? '';
        this.isFinal = payload.isCalAdjustFinal;
        const reportContent = payload.reportContentResponse?.reportContentItems ?? [];
        this.calResponseCombinedData = this.buildCombinedData(
          reportContent,
          payload.calculationResult,
          payload.calculationAdjustedResult
        );
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Nudge horizontal scroll to avoid initial sticky-border rendering glitch.
    requestAnimationFrame(() => {
      const scrollEl = this.statusSummaryScrollRef?.nativeElement;
      if (scrollEl) {
        scrollEl.scrollLeft = 1;
      }
    });
  }

  trackAssayRow = (_index: number, assay: StatusAssayData) =>
    `${assay?.reportContentItems?.reportContentItemId ?? 'unknown'}-${assay?.valueAssigned ?? 'Original'}`;

  isMeanColumnAssay(materialItem: StatusAssayData | undefined): boolean {
    if (!materialItem) {
      return false;
    }
    return materialItem.isMeanColumn ?? this.isMeanReportItem(materialItem.reportContentItems?.reportContentItemId);
  }

  getRunValidityDisplay(materialItem: StatusAssayData | undefined, value: unknown): string {
    if (this.isMeanColumnAssay(materialItem)) {
      return '-';
    }
    return displayOrDash(value);
  }

  getRunValidityStatusClass(materialItem: StatusAssayData | undefined, value: string | undefined | null): string {
    if (this.isMeanColumnAssay(materialItem)) {
      return '';
    }
    return this.getStatusClassFromResult(value ?? undefined);
  }

  /**
   * Reference kit lot, and a column assigned against an adjusted calibration. The template used to
   * repeat this object literal on each of the four header rows.
   */
  getColumnClasses(materialItem: StatusAssayData): Record<string, boolean> {
    return {
      'selected-kit-highlight': this.refKitLot !== '' && materialItem?.reportContentItems?.lot === this.refKitLot,
      [adjustedColumnClass(this.isFinal)]: materialItem.valueAssigned === 'AdjustedCalculate'
    };
  }

  private buildCombinedData(
    reportContentItems: ReportContentItem[],
    calculationResult: CalculationResult | null,
    calculationAdjustedResult: CalculationResult | null
  ): StatusAssayData[] {
    if (!reportContentItems?.length) {
      return [];
    }

    const sortedReportContent = [...reportContentItems].sort((a, b) => this.compareReportContentItems(a, b));
    const originalRunStatusMap = this.indexRunStatusFromResult(calculationResult);
    const adjustedRunStatusMap = this.indexRunStatusFromResult(calculationAdjustedResult);

    const originalData = sortedReportContent.map((reportContentItem) => ({
      reportContentItems: reportContentItem,
      runStatus: originalRunStatusMap.get(reportContentItem.reportContentItemId),
      valueAssigned: 'OriginalCalculate',
      isMeanColumn: this.isMeanReportItem(reportContentItem.reportContentItemId)
    }));

    const adjustedData = calculationAdjustedResult
      ? sortedReportContent.map((reportContentItem) => ({
        reportContentItems: reportContentItem,
        runStatus: adjustedRunStatusMap.get(reportContentItem.reportContentItemId),
        valueAssigned: 'AdjustedCalculate',
        isMeanColumn: this.isMeanReportItem(reportContentItem.reportContentItemId)
      }))
      : [];

    // A Final run has no unadjusted baseline, so no unadjusted columns are shown.
    const combined = this.isFinal ? adjustedData : [...originalData, ...adjustedData];
    return combined.sort((left, right) => this.compareAssayData(left, right));
  }

  private indexRunStatusFromResult(calculationResult: CalculationResult | null): Map<string, RunStatus> {
    const map = new Map<string, RunStatus>();
    (calculationResult?.runStatus ?? []).forEach(runStatus => {
      if (runStatus?.reportContentItemId) {
        map.set(runStatus.reportContentItemId, runStatus);
      }
    });
    return map;
  }

  private compareReportContentItems(a: ReportContentItem, b: ReportContentItem): number {
    const baseCompare = this.compareReportContentBase(a, b);
    if (baseCompare !== 0) {
      return baseCompare;
    }

    const runCompare = (a.runId ?? '').localeCompare(b.runId ?? '');
    if (runCompare !== 0) {
      return runCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareAssayData(left: StatusAssayData, right: StatusAssayData): number {
    const baseCompare = this.compareReportContentBase(left.reportContentItems, right.reportContentItems);
    if (baseCompare !== 0) {
      return baseCompare;
    }

    const adjustCompare = this.getAssayAdjustmentRank(left) - this.getAssayAdjustmentRank(right);
    if (adjustCompare !== 0) {
      return adjustCompare;
    }

    const runCompareWithMean = this.compareRunIdWithMeanLast(left, right);
    if (runCompareWithMean !== 0) {
      return runCompareWithMean;
    }

    return 0;
  }

  private compareReportContentBase(a: ReportContentItem, b: ReportContentItem): number {
    const regionCompare = this.compareBeadRegions(a.region, b.region);
    if (regionCompare !== 0) {
      return regionCompare;
    }

    const refLot = (this.refKitLot ?? '').trim();
    const aLot = (a.lot ?? '');
    const bLot = (b.lot ?? '');
    if (refLot) {
      const aIsRef = aLot === refLot;
      const bIsRef = bLot === refLot;
      if (aIsRef !== bIsRef) {
        return aIsRef ? -1 : 1;
      }
    }

    const kitCompare = aLot.localeCompare(bLot);
    if (kitCompare !== 0) {
      return kitCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareRunIdWithMeanLast(left: StatusAssayData, right: StatusAssayData): number {
    const leftIsMean = this.isMeanColumnAssay(left);
    const rightIsMean = this.isMeanColumnAssay(right);
    if (leftIsMean !== rightIsMean) {
      return leftIsMean ? 1 : -1;
    }
    return (left.reportContentItems.runId ?? '').localeCompare(right.reportContentItems.runId ?? '');
  }

  private getAssayAdjustmentRank(item: StatusAssayData): number {
    return item.valueAssigned === 'AdjustedCalculate' ? 1 : 0;
  }

  private compareBeadRegions(left: string | number | undefined, right: string | number | undefined): number {
    const leftRegion = this.parseRegionValue(left);
    const rightRegion = this.parseRegionValue(right);
    const leftInternal = this.internalQcBeadRegions.has(leftRegion);
    const rightInternal = this.internalQcBeadRegions.has(rightRegion);
    if (leftInternal !== rightInternal) {
      return leftInternal ? 1 : -1;
    }
    return leftRegion - rightRegion;
  }

  private parseRegionValue(region: string | number | undefined): number {
    if (typeof region === 'number') {
      return Number.isNaN(region) ? 0 : region;
    }
    const parsed = Number(region);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private isMeanReportItem(reportContentItemId: string | undefined): boolean {
    return !!reportContentItemId && reportContentItemId.startsWith('Mean_');
  }
}
